import { useMemo } from "react";

// Lightweight, dependency-free password strength hint.
// Returns a 0-4 score from length + character variety.
export function scorePassword(pw = "") {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  return Math.min(score, 4);
}

const META = [
  { label: "Too weak", color: "bg-red-500", text: "text-red-600" },
  { label: "Weak", color: "bg-red-500", text: "text-red-600" },
  { label: "Fair", color: "bg-amber-500", text: "text-amber-600" },
  { label: "Good", color: "bg-lime-500", text: "text-lime-600" },
  { label: "Strong", color: "bg-green-600", text: "text-green-700" },
];

export const PasswordStrengthMeter = ({ password = "" }) => {
  const score = useMemo(() => scorePassword(password), [password]);
  if (!password) return null;
  const meta = META[score];
  const hints = [];
  if (password.length < 8) hints.push("use 8+ characters");
  if (!(/[a-z]/.test(password) && /[A-Z]/.test(password))) hints.push("mix upper & lowercase");
  if (!/\d/.test(password)) hints.push("add a number");
  if (!/[^A-Za-z0-9]/.test(password)) hints.push("add a symbol");

  return (
    <div className="mt-2" data-testid="password-strength">
      <div className="flex gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-1.5 flex-1 rounded-full bg-black/10 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${i < score ? meta.color : "bg-transparent"}`}
              style={{ width: i < score ? "100%" : "0%" }}
            />
          </div>
        ))}
      </div>
      <p className={`mt-1 text-xs font-medium ${meta.text}`} data-testid="password-strength-label">
        {meta.label}
        {score < 4 && hints.length > 0 && (
          <span className="text-muted-foreground font-normal"> — {hints.slice(0, 2).join(", ")}</span>
        )}
      </p>
    </div>
  );
};

export default PasswordStrengthMeter;
